import { type FormEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import {
  type FieldErrors,
  type FieldValues,
  FormProvider,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiErrorCode, applyApiErrorsToForm, hasFieldErrors, isApiError } from '@/shared/api';
import { Button } from '../Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogLead,
  DialogNote,
  type DialogWidth,
  FormAlert,
} from '../Dialog';
import { getErrorMessage } from '../errorMessage';
import { FieldNames, type FieldRegistry, FieldRegistryContext } from './fieldRegistry';
import { FormBusyContext, type FormBusyRegistry } from './formBusy';

/**
 * Кнопка отправки формы с собственной подготовкой значений (конструктор рассылок: «Черновик» и
 * «Запланировать» отправляют одну форму с разным `intent`): `prepare` вызывается перед проверкой
 * формы (например, `form.setValue('intent', 'draft')`), дальше — общий `onSubmit` окна.
 */
export interface FormSubmitAction {
  key: string;
  label: ReactNode;
  variant?: 'solid' | 'ghost';
  prepare?: () => void;
  disabled?: boolean;
}

export interface FormDialogProps<TValues extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TValues>;
  /** Сохранение (мутация). Ошибка ApiError 422 раскладывается по полям, прочие — плашкой. */
  onSubmit: (values: TValues) => Promise<unknown>;
  width?: DialogWidth;
  overline?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  /** Примечание под полями (плашка gold-wash-2). */
  note?: ReactNode;
  /** Пояснение в подвале слева от кнопок. */
  footNote?: ReactNode;
  /** Подпись единственной кнопки отправки (без `submitActions`). */
  submitLabel?: ReactNode;
  /**
   * Несколько кнопок отправки вместо одной `submitLabel`, слева направо; последняя — основная
   * (Enter в поле формы отправляет её).
   */
  submitActions?: readonly FormSubmitAction[];
  submitVariant?: 'solid' | 'danger';
  /**
   * Сохранение недоступно (роль или неподключённая функция): кнопка неактивна, причина —
   * в `note` или `lead` окна.
   */
  submitDisabled?: boolean;
  /** Текст плашки при ошибках клиентской проверки вместо «Полей заполнено неверно: N…». */
  invalidMessage?: string;
  cancelLabel?: ReactNode;
  /**
   * Действия записи в подвале перед «Отмена» (справочник уровней: «Удалить» — danger, прототип
   * `dictrow`): кнопки передаёт раздел, только разрешённые флагами `can`.
   */
  extraActions?: ReactNode;
  children: ReactNode;
}

/** Число полей с ошибкой: лист дерева ошибок RHF — объект с `type`. */
function countErrors(errors: unknown): number {
  if (typeof errors !== 'object' || errors === null) return 0;
  if ('type' in errors && typeof errors.type === 'string') return 1;
  let count = 0;
  for (const [key, value] of Object.entries(errors)) {
    if (key === 'root' || key === 'ref') continue;
    count += countErrors(value);
  }
  return count;
}

/**
 * Окно формы (README «Оверлеи»): RHF + zod, плашка ошибки формы над полями
 * («Одно поле заполнено неверно — отмечено ниже.» / «Полей заполнено неверно: N…»),
 * подстановка ошибок 422 из API в поля по имени (applyApiErrorsToForm).
 */
export function FormDialog<TValues extends FieldValues>({
  open,
  onOpenChange,
  form,
  onSubmit,
  width = 560,
  overline,
  title,
  lead,
  note,
  footNote,
  submitLabel,
  submitActions,
  submitVariant = 'solid',
  submitDisabled = false,
  invalidMessage,
  cancelLabel,
  extraActions,
  children,
}: FormDialogProps<TValues>) {
  const { t } = useTranslation('form');
  const [alert, setAlert] = useState<string | null>(null);
  /** Какая из `submitActions` отправляет форму (индикатор загрузки — только на ней). */
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const fieldNames = useRef(new FieldNames());
  const registry = useMemo<FieldRegistry>(
    () => ({ register: (name, target) => fieldNames.current.register(name, target) }),
    [],
  );
  const [busyFields, setBusyFields] = useState<ReadonlySet<string>>(() => new Set());
  const busyRegistry = useMemo<FormBusyRegistry>(
    () => ({
      setBusy: (id, busy) =>
        setBusyFields((current) => {
          if (current.has(id) === busy) return current;
          const next = new Set(current);
          if (busy) next.add(id);
          else next.delete(id);
          return next;
        }),
    }),
    [],
  );
  const busy = busyFields.size > 0;
  const submitting = form.formState.isSubmitting;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && submitting) return;
      if (!next) setAlert(null);
      onOpenChange(next);
    },
    [onOpenChange, submitting],
  );

  async function submit(values: TValues) {
    // Поле занято (идёт загрузка файла) — кнопка неактивна; Enter в поле тоже не отправляет.
    if (busy) return;
    setAlert(null);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (error) {
      // Отмена (запрос прерван или пользователь отказался в окне подтверждения) — окно остаётся
      // открытым без плашки ошибки.
      if (isApiError(error) && error.code === ApiErrorCode.Aborted) return;
      if (hasFieldErrors(error)) {
        const applied = applyApiErrorsToForm(
          error,
          form.setError,
          fieldNames.current.names,
          fieldNames.current.aliases,
        );
        const extra = applied.unmatched.map((item) => item.message).join(' ');
        const summary =
          applied.fieldErrors > 0 ? t('alert.invalid', { count: applied.fieldErrors }) : '';
        setAlert([summary, extra].filter(Boolean).join(' ') || getErrorMessage(error));
        return;
      }
      setAlert(getErrorMessage(error));
    }
  }

  function invalid(errors: FieldErrors<TValues>) {
    const count = countErrors(errors);
    if (count === 0) setAlert(null);
    else setAlert(invalidMessage ?? t('alert.invalid', { count }));
  }

  const primaryAction = submitActions?.at(-1);

  /** Кнопка из `submitActions`: подготовить значения и отправить форму общим `onSubmit`. */
  function runAction(action: FormSubmitAction) {
    action.prepare?.();
    setActiveAction(action.key);
    void form.handleSubmit(submit, invalid)();
  }

  function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    // Enter в поле или основная кнопка — последняя из `submitActions`.
    if (primaryAction) {
      primaryAction.prepare?.();
      setActiveAction(primaryAction.key);
    }
    return form.handleSubmit(submit, invalid)(event);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent width={width} preventClose={submitting} noDescription={!lead}>
        <FormProvider {...form}>
          <FormBusyContext.Provider value={busyRegistry}>
            <FieldRegistryContext.Provider value={registry}>
              <form noValidate onSubmit={handleFormSubmit} className="flex min-h-0 flex-1 flex-col">
                <DialogHeader overline={overline} title={title} hideClose={submitting} />
                <DialogBody>
                  {lead ? <DialogLead>{lead}</DialogLead> : null}
                  {alert ? <FormAlert>{alert}</FormAlert> : null}
                  {children}
                  {note ? <DialogNote>{note}</DialogNote> : null}
                </DialogBody>
                <DialogFooter note={footNote}>
                  {extraActions}
                  <Button
                    variant="ghost"
                    onClick={() => handleOpenChange(false)}
                    disabled={submitting}
                  >
                    {cancelLabel ?? t('cancel')}
                  </Button>
                  {submitActions ? (
                    submitActions.map((action) => {
                      const primary = action === primaryAction;
                      const running = submitting && activeAction === action.key;
                      return (
                        <Button
                          key={action.key}
                          type={primary ? 'submit' : 'button'}
                          variant={action.variant ?? 'solid'}
                          loading={running}
                          disabled={
                            busy || submitDisabled || action.disabled || (submitting && !running)
                          }
                          onClick={primary ? undefined : () => runAction(action)}
                        >
                          {action.label}
                        </Button>
                      );
                    })
                  ) : (
                    <Button
                      type="submit"
                      variant={submitVariant}
                      loading={submitting}
                      disabled={busy || submitDisabled}
                    >
                      {submitLabel}
                    </Button>
                  )}
                </DialogFooter>
              </form>
            </FieldRegistryContext.Provider>
          </FormBusyContext.Provider>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
